import {
  sidebarOverlayActiveAtom,
  sidebarPanelContentAtom,
  sidebarPanelOverlayLockAtom,
  sidebarPanelPinnedAtom,
  type SidebarPanelContent,
} from '@/atoms/sidebarPanelAtoms';
import {
  SIDEBAR_OVERLAY_OPEN_ATTR,
  SIDEBAR_OVERLAY_OPEN_VALUE,
  SIDEBAR_OVERLAY_TRIGGER_ATTR,
  SIDEBAR_OVERLAY_TRIGGER_VALUE,
} from '@/components/layout/Navigation/sidebar-panel/sidebar-overlay-constants';
import { IS_SIDEBAR_PANEL_ENABLED } from '@/features/flags/legacy-flags';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

import {
  clearSharedHideTimeout,
  clearSharedShowTimeout,
  getSharedSidebarElement,
  HIDE_DEBOUNCE_MS,
  setSharedHideTimeout,
  setSharedShowTimeout,
  SHOW_DEBOUNCE_MS,
} from './sidebar-panel-shared-state';

/**
 * Retry delay when the overlay lock blocks a content switch.
 * Must exceed the HoverCard's 300ms close delay (Radix default) so the lock
 * is released by the time the retry fires. Uses the shared show timeout so
 * any subsequent nav-item hover or leave will cancel it automatically.
 */
const OVERLAY_LOCK_RETRY_MS = 350;

/**
 * Selector for overlay triggers that are currently open.
 * Matches triggers marked with the sidebar overlay attribute that have
 * an open data-state, open overlay marker, or expanded aria state.
 */
const OPEN_OVERLAY_SELECTOR = [
  `[${SIDEBAR_OVERLAY_TRIGGER_ATTR}="${SIDEBAR_OVERLAY_TRIGGER_VALUE}"][data-state="open"]`,
  `[${SIDEBAR_OVERLAY_TRIGGER_ATTR}="${SIDEBAR_OVERLAY_TRIGGER_VALUE}"][${SIDEBAR_OVERLAY_OPEN_ATTR}="${SIDEBAR_OVERLAY_OPEN_VALUE}"]`,
  `[${SIDEBAR_OVERLAY_TRIGGER_ATTR}="${SIDEBAR_OVERLAY_TRIGGER_VALUE}"][aria-expanded="true"]`,
].join(',');

export interface UseSidebarPanelNavReturn {
  /** Current content being displayed (or null if hidden) */
  content: SidebarPanelContent;
  /** Handler for when mouse enters a nav item */
  handleNavItemMouseEnter: (content: SidebarPanelContent) => void;
  /** Handler for when mouse leaves a nav item */
  handleNavItemMouseLeave: () => void;
}

/**
 * Lightweight hook for NavItem sidebar panel coordination.
 *
 * Unlike `useSidebarPanel`, this hook only subscribes to `sidebarPanelContentAtom`,
 * avoiding unnecessary re-renders from pin state, overlay lock, or route changes.
 * Other atom values are read imperatively (via Jotai store) inside timeout callbacks
 * where only the current-at-execution-time value matters.
 *
 * Uses the same module-level shared state (timers, sidebar element) as the main hook,
 * ensuring cross-component coordination still works.
 */
export function useSidebarPanelNav(): UseSidebarPanelNavReturn {
  const content = useAtomValue(sidebarPanelContentAtom);
  const setHoverContent = useSetAtom(sidebarPanelContentAtom);
  const store = useStore();
  const isEnabled = IS_SIDEBAR_PANEL_ENABLED();

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleNavItemMouseEnter = useCallback(
    (newContent: SidebarPanelContent) => {
      if (!isEnabled || !newContent) return;

      clearSharedHideTimeout();
      clearSharedShowTimeout();
      setSharedShowTimeout(
        setTimeout(() => {
          if (!isMountedRef.current) return;

          const currentContent = store.get(sidebarPanelContentAtom);

          // Overlay lock: block content switch when a sessions overlay is open.
          // The lock atom is only set by sessions-panel overlay components,
          // so lock > 0 guarantees sessions is the visible content — even in
          // pinned mode where hoverContent is null and sessions shows via route.
          //
          // When blocked, schedule a retry. Transient overlays (HoverCard) close
          // automatically when the mouse leaves, releasing the lock — the retry
          // will then proceed. Persistent overlays (combobox) keep the lock active
          // so the retry becomes a no-op.
          if (newContent !== 'sessions') {
            const overlayLocked = store.get(sidebarPanelOverlayLockAtom) > 0;
            if (overlayLocked) {
              setSharedShowTimeout(
                setTimeout(() => {
                  if (!isMountedRef.current) return;
                  if (store.get(sidebarPanelOverlayLockAtom) > 0) return;
                  setHoverContent(newContent);
                }, OVERLAY_LOCK_RETRY_MS)
              );
              return;
            }
          }

          // DOM fallback: for components using data-attribute detection.
          // In pinned mode (hoverContent=null), also check because setting
          // hoverContent would hide the route-based sessions content.
          const isPinned = store.get(sidebarPanelPinnedAtom);
          const isEffectivelySwitchingFromSessions =
            newContent !== 'sessions' &&
            (currentContent === 'sessions' || (currentContent === null && isPinned));

          if (isEffectivelySwitchingFromSessions) {
            const sidebar = getSharedSidebarElement();
            if (sidebar) {
              const hasOpenTrigger = sidebar.querySelector(OPEN_OVERLAY_SELECTOR);
              const hasPortal = document.body.querySelector(
                '[data-radix-popper-content-wrapper]'
              );
              if (hasOpenTrigger && hasPortal) return;
            }
          }

          setHoverContent(newContent);
        }, SHOW_DEBOUNCE_MS)
      );
    },
    [isEnabled, setHoverContent, store]
  );

  const handleNavItemMouseLeave = useCallback(() => {
    if (!isEnabled) return;

    clearSharedShowTimeout();
    clearSharedHideTimeout();
    setSharedHideTimeout(
      setTimeout(() => {
        if (!isMountedRef.current) return;

        const currentContent = store.get(sidebarPanelContentAtom);

        // Check overlay lock for sessions-sensitive content
        if (currentContent === 'sessions') {
          const overlayLocked = store.get(sidebarPanelOverlayLockAtom) > 0;
          if (overlayLocked) return;

          // If MutationObserver is watching an overlay close, defer hide
          if (store.get(sidebarOverlayActiveAtom)) return;
        }

        // If the panel is still being hovered, don't hide
        const sidebar = getSharedSidebarElement();
        if (sidebar?.matches(':hover')) return;

        setHoverContent(null);
      }, HIDE_DEBOUNCE_MS)
    );
  }, [isEnabled, setHoverContent, store]);

  return { content, handleNavItemMouseEnter, handleNavItemMouseLeave };
}
